<script setup>
import { useRouter } from 'vue-router';
import { ref } from 'vue';

// new modal components
import InvestorModal from './InvestorModal.vue';
import PmlaModal from './PmlaModal.vue';
import DisclaimerModal from './DisclaimerModal.vue';

const router = useRouter();

const goBack = () => {
  router.push('/');
};

// Modal state and PDF paths
const showInvestorModal = ref(false);
const showPmlaModal = ref(false);
const showDisclaimerModal = ref(false);
// These PDF files should be placed in public/assets/ with the exact filenames below
const investorPdfPath = '/assets/Investor_Charter_IA_Simple (1).pdf';
const pmlaPdfPath = '/assets/VIVEK_GUPTA_PMLA_POLICY_IA_FINAL1.pdf';

const disclosureData = {
  completeName: "VIVEK GUPTA",
  registrationType: "Individual",
  iaCategory: "Investment Adviser (IA)",
  registrationNumber: "INA000020554",
  registrationValidity: "Jul 11, 2025 – Perpetual",
  email: "vivek203@yahoo.com",
  telephone: "+91 9958836060",
  fax: "+91 9958836060",
  registeredAddress: "Flat No-65, RPS, Sheikh Sarai Phase I, New Delhi, National Capital Territory of Delhi, 110017",
  correspondenceAddress: "Flat No-65, RPS, Sheikh Sarai Phase I, New Delhi, National Capital Territory of Delhi, 110017",
  principalOfficerName: "Vivek Gupta",
  principalOfficerEmail: "vivek203@yahoo.com",
  principalOfficerPhone: "+91 9958836060",
  sebiLocalOfficeName: "SEBI Northern Regional Office (NRO)",
  sebiLocalOfficeAddress: "NBCC Complex, Office Tower-1, 8th Floor, Plate B, East Kidwai Nagar, New Delhi – 110023",
  sebiLocalOfficeTelephone: "+91-011-69012998",
  sebiLocalOfficeEmail: "sebinro@sebi.gov.in"
};

// Data for Month Ending (per source)
const monthEndingData = [
  { source: 'Directly from Investors', pendingLastMonth: 0, received: 1, resolved: 1, totalPending: 0, pendingMoreThan3Months: 0, avgResolutionDays: 0 },
  { source: 'SEBI SCORES', pendingLastMonth: 0, received: 0, resolved: 0, totalPending: 0, pendingMoreThan3Months: 0, avgResolutionDays: 0 },
  { source: 'Other Sources', pendingLastMonth: 0, received: 0, resolved: 0, totalPending: 0, pendingMoreThan3Months: 0, avgResolutionDays: 0 }
];

const monthEndingTotals = monthEndingData.reduce((acc, row) => {
  acc.pendingLastMonth += row.pendingLastMonth;
  acc.received += row.received;
  acc.resolved += row.resolved;
  acc.totalPending += row.totalPending;
  acc.pendingMoreThan3Months += row.pendingMoreThan3Months;
  return acc;
}, { pendingLastMonth: 0, received: 0, resolved: 0, totalPending: 0, pendingMoreThan3Months: 0 });

// Trend of Monthly Disposal of Complaints (Month 1..12) — put the single complaint in Month 9
const monthlyTrend = Array.from({ length: 12 }, (_, i) => ({
  month: `Month ${i + 1}`,
  carriedFromLastMonth: 0,
  received: i === 8 ? 1 : 0, // Month 9 -> index 8
  resolved: i === 8 ? 1 : 0,
  pending: 0
}));

const monthlyGrandTotals = monthlyTrend.reduce((acc, row) => {
  acc.carriedFromLastMonth += row.carriedFromLastMonth;
  acc.received += row.received;
  acc.resolved += row.resolved;
  acc.pending += row.pending;
  return acc;
}, { carriedFromLastMonth: 0, received: 0, resolved: 0, pending: 0 });

// Trend of Annual Disposal of Complaints
const annualTrend = [
  { year: '2026–27', carriedForward: 0, received: monthlyGrandTotals.received, resolved: monthlyGrandTotals.resolved, pending: monthlyGrandTotals.pending }
];

const annualGrandTotals = annualTrend.reduce((acc, row) => {
  acc.carriedForward += row.carriedForward || 0;
  acc.received += row.received || 0;
  acc.resolved += row.resolved || 0;
  acc.pending += row.pending || 0;
  return acc;
}, { carriedForward: 0, received: 0, resolved: 0, pending: 0 });

</script>

<template>
  <div class="sebi-disclosures-page">
    <!-- Header Section -->
    <div class="disclosure-header">
      <div class="header-container">
        <button class="back-btn" @click="goBack">
          <i class="pi pi-arrow-left"></i> Back to Home
        </button>
        <h1 class="main-title">Regulatory Disclosures</h1>
        <p class="subtitle">SEBI Registered Investment Adviser</p>
        <div class="reg-number">Reg. No: {{ disclosureData.registrationNumber }}</div>
      </div>
    </div>

    <!-- Disclaimer Section -->
    <div class="disclaimer-section">
      <div class="disclaimer-box">
        <i class="pi pi-info-circle"></i>
        <p>Registration granted by SEBI, and the certificate of registration, does not in any manner guarantee performance of the intermediary or provide any assurance of returns to investors.</p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="disclosure-content">
      <!-- Complaint Status Tracker (detailed, as per provided template) -->
      <section class="disclosure-section complaint-full-tracker">
        <h2 class="section-title">
          <i class="pi pi-chart-line"></i>
          Complaint Status Tracker
        </h2>
        <p class="tracker-note">This table is displayed on the AssetNivesh website home page in compliance with SEBI Circular SEBI/HO/IMD/DF1/CIR/P/2019/169 on ‘Display of complaints status on website’ for Vivek Gupta SEBI RIA INA000020554. It is updated by the 7th of every month, starting from the registration month (July 2025).</p>

        <!-- 01 Data for Month Ending -->
        <h3 class="tracker-subtitle">01 Data for Month Ending</h3>
        <p class="tracker-subtext">Figures below will be updated monthly once client onboarding begins.</p>

        <div class="tracker-table-wrap">
          <table class="tracker-table">
            <thead>
              <tr>
                <th>Received from</th>
                <th>Pending (last month)</th>
                <th>Received</th>
                <th>Resolved</th>
                <th>Total pending</th>
                <th>Pending &gt; 3 months</th>
                <th>Avg. resolution (days)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in monthEndingData" :key="idx">
                <td>{{ row.source }}</td>
                <td class="num">{{ row.pendingLastMonth }}</td>
                <td class="num">{{ row.received }}</td>
                <td class="num">{{ row.resolved }}</td>
                <td class="num">{{ row.totalPending }}</td>
                <td class="num">{{ row.pendingMoreThan3Months }}</td>
                <td class="num">{{ row.avgResolutionDays }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="grand-total">
                <td>Grand Total</td>
                <td class="num">{{ monthEndingTotals.pendingLastMonth }}</td>
                <td class="num">{{ monthEndingTotals.received }}</td>
                <td class="num">{{ monthEndingTotals.resolved }}</td>
                <td class="num">{{ monthEndingTotals.totalPending }}</td>
                <td class="num">{{ monthEndingTotals.pendingMoreThan3Months }}</td>
                <td class="num">0</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- 02 Trend of Monthly Disposal of Complaints -->
        <h3 class="tracker-subtitle">02 Trend of Monthly Disposal of Complaints</h3>
        <div class="tracker-table-wrap">
          <table class="tracker-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Carried from last month</th>
                <th>Received</th>
                <th>Resolved</th>
                <th>Pending</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(m, idx) in monthlyTrend" :key="idx">
                <td>{{ m.month }}</td>
                <td class="num">{{ m.carriedFromLastMonth }}</td>
                <td class="num">{{ m.received }}</td>
                <td class="num">{{ m.resolved }}</td>
                <td class="num">{{ m.pending }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="grand-total">
                <td>Grand Total</td>
                <td class="num">—</td>
                <td class="num">{{ monthlyGrandTotals.received }}</td>
                <td class="num">{{ monthlyGrandTotals.resolved }}</td>
                <td class="num">{{ monthlyGrandTotals.pending }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- 03 Trend of Annual Disposal of Complaints -->
        <h3 class="tracker-subtitle">03 Trend of Annual Disposal of Complaints</h3>
        <div class="tracker-table-wrap">
          <table class="tracker-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Carried forward</th>
                <th>Received</th>
                <th>Resolved</th>
                <th>Pending</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(a, idx) in annualTrend" :key="idx">
                <td>{{ a.year }}</td>
                <td class="num">{{ a.carriedForward }}</td>
                <td class="num">{{ a.received }}</td>
                <td class="num">{{ a.resolved }}</td>
                <td class="num">{{ a.pending }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="grand-total">
                <td>Grand Total</td>
                <td class="num">—</td>
                <td class="num">{{ annualGrandTotals.received }}</td>
                <td class="num">{{ annualGrandTotals.resolved }}</td>
                <td class="num">{{ annualGrandTotals.pending }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <!-- PDF Buttons + Modals -->
      <div class="pdf-buttons" style="margin-bottom:24px; display:flex; gap:12px; align-items:center;">
        <button class="doc-btn" @click="showInvestorModal = true">Investor Charter</button>
        <button class="doc-btn" @click="showPmlaModal = true">PMLA Policy</button>
        <button class="doc-btn" @click="showDisclaimerModal = true">Disclaimer</button>
      </div>

      <!-- Use separate modal components to show PDFs -->
      <InvestorModal :show="showInvestorModal" :pdfPath="investorPdfPath" title="Investor Charter" @close="showInvestorModal = false" />
      <PmlaModal :show="showPmlaModal" :pdfPath="pmlaPdfPath" title="PMLA Policy" @close="showPmlaModal = false" />
      <DisclaimerModal :show="showDisclaimerModal" title="Disclaimer" @close="showDisclaimerModal = false" />

      <!-- Registration Details -->
      <section class="disclosure-section">
        <h2 class="section-title">
          <i class="pi pi-id-card"></i>
          Registration Details
        </h2>
        <div class="info-grid">
          <div class="info-item">
            <label>Complete Name of Investment Adviser</label>
            <div class="info-value">{{ disclosureData.completeName }}</div>
          </div>
          <div class="info-item">
            <label>Type of Registration</label>
            <div class="info-value">{{ disclosureData.registrationType }}</div>
          </div>
          <div class="info-item">
            <label>Category</label>
            <div class="info-value">{{ disclosureData.iaCategory }}</div>
          </div>
          <div class="info-item">
            <label>Registration Number</label>
            <div class="info-value highlight">{{ disclosureData.registrationNumber }}</div>
          </div>
          <div class="info-item full-width">
            <label>Validity of Registration</label>
            <div class="info-value">{{ disclosureData.registrationValidity }}</div>
          </div>
        </div>
      </section>

      <!-- Contact Information -->
      <section class="disclosure-section">
        <h2 class="section-title">
          <i class="pi pi-phone"></i>
          Contact Information
        </h2>
        <div class="info-grid">
          <div class="info-item full-width">
            <label>Registered Address</label>
            <div class="info-value">{{ disclosureData.registeredAddress }}</div>
          </div>
          <div class="info-item full-width">
            <label>Correspondence Address</label>
            <div class="info-value">{{ disclosureData.correspondenceAddress }}</div>
          </div>
          <div class="info-item">
            <label>Telephone</label>
            <div class="info-value">
              <a :href="`tel:${disclosureData.telephone}`">{{ disclosureData.telephone }}</a>
            </div>
          </div>
          <div class="info-item">
            <label>Fax</label>
            <div class="info-value">{{ disclosureData.fax }}</div>
          </div>
          <div class="info-item full-width">
            <label>Email</label>
            <div class="info-value">
              <a :href="`mailto:${disclosureData.email}`">{{ disclosureData.email }}</a>
            </div>
          </div>
        </div>
      </section>

      <!-- Principal Officer Details -->
      <section class="disclosure-section">
        <h2 class="section-title">
          <i class="pi pi-user"></i>
          Principal Officer Details
        </h2>
        <div class="info-grid">
          <div class="info-item">
            <label>Name</label>
            <div class="info-value">{{ disclosureData.principalOfficerName }}</div>
          </div>
          <div class="info-item">
            <label>Contact Number</label>
            <div class="info-value">
              <a :href="`tel:${disclosureData.principalOfficerPhone}`">{{ disclosureData.principalOfficerPhone }}</a>
            </div>
          </div>
          <div class="info-item full-width">
            <label>Email</label>
            <div class="info-value">
              <a :href="`mailto:${disclosureData.principalOfficerEmail}`">{{ disclosureData.principalOfficerEmail }}</a>
            </div>
          </div>
        </div>
      </section>

      <!-- SEBI Regional Office -->
      <section class="disclosure-section">
        <h2 class="section-title">
          <i class="pi pi-building"></i>
          Corresponding SEBI Regional Office
        </h2>
        <div class="info-grid">
          <div class="info-item full-width">
            <label>Office Name</label>
            <div class="info-value">{{ disclosureData.sebiLocalOfficeName }}</div>
          </div>
          <div class="info-item full-width">
            <label>Address</label>
            <div class="info-value">{{ disclosureData.sebiLocalOfficeAddress }}</div>
          </div>
          <div class="info-item">
            <label>Telephone</label>
            <div class="info-value">
              <a :href="`tel:${disclosureData.sebiLocalOfficeTelephone}`">{{ disclosureData.sebiLocalOfficeTelephone }}</a>
            </div>
          </div>
          <div class="info-item">
            <label>Email</label>
            <div class="info-value">
              <a :href="`mailto:${disclosureData.sebiLocalOfficeEmail}`">{{ disclosureData.sebiLocalOfficeEmail }}</a>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact & Grievance Redressal -->
      <section class="disclosure-section grievance-section">
        <h2 class="section-title">
          <i class="pi pi-comments"></i>
          Contact & Grievance Redressal
        </h2>
        <div class="grievance-content">
          <p class="grievance-text">
            For any queries, clarifications, or grievances related to investment advisory services, clients may contact the Principal Officer:
          </p>

          <div class="contact-box">
            <div class="contact-item">
              <strong>Principal Officer:</strong> {{ disclosureData.principalOfficerName }}
            </div>
            <div class="contact-item">
              <strong>Email:</strong>
              <a :href="`mailto:${disclosureData.principalOfficerEmail}`">{{ disclosureData.principalOfficerEmail }}</a>
            </div>
            <div class="contact-item">
              <strong>Phone:</strong>
              <a :href="`tel:${disclosureData.principalOfficerPhone}`">{{ disclosureData.principalOfficerPhone }}</a>
            </div>
          </div>

          <p class="grievance-text">
            Clients are encouraged to first reach out to the Investment Adviser for resolution of any concerns.
            If the grievance is not resolved satisfactorily, clients may escalate the matter through
            <a href="https://scores.sebi.gov.in/" target="_blank" rel="noopener noreferrer">SEBI's SCORES platform</a>
            in accordance with SEBI guidelines.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.sebi-disclosures-page {
  min-height: 100vh;
  background: #f8fafc;
}

.disclosure-header {
  background: linear-gradient(135deg, #0e38d2 0%, #4fb6ed 100%);
  color: white;
  padding: 40px 20px;
  text-align: center;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
}

.back-btn {
  position: absolute;
  left: 0;
  top: 0;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 10px 0;
}

.subtitle {
  font-size: 1.2rem;
  margin: 0 0 10px 0;
  opacity: 0.95;
}

.reg-number {
  font-size: 1rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  margin-top: 10px;
}

.disclaimer-section {
  max-width: 1200px;
  margin: -20px auto 30px;
  padding: 0 20px;
}

.disclaimer-box {
  background: #fef3c7;
  border: 2px solid #fbbf24;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.disclaimer-box i {
  font-size: 1.5rem;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 2px;
}

.disclaimer-box p {
  margin: 0;
  font-size: 0.95rem;
  color: #78350f;
  line-height: 1.6;
}

.disclosure-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.disclosure-section {
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 25px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e5e7eb;
}

.section-title i {
  color: #3b82f6;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  color: #1f2937;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.info-value.highlight {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1e40af;
  font-weight: 600;
}

.info-value a {
  color: #3b82f6;
  text-decoration: none;
}

.info-value a:hover {
  text-decoration: underline;
}

/* Complaint tracker styles */
.complaint-tracker .tracker-grid {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.complaint-tracker .tracker-item {
  flex: 1 1 200px;
  min-width: 160px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
}

.complaint-tracker .tracker-item label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.complaint-tracker .tracker-value {
  font-size: 1.5rem;
  font-weight: 800;
  margin-top: 8px;
  color: #0f172a;
}

.complaint-tracker .tracker-item.received .tracker-value { color: #1e40af; }
.complaint-tracker .tracker-item.disposed .tracker-value { color: #065f46; }
.complaint-tracker .tracker-item.pending .tracker-value { color: #b45309; }
.complaint-tracker .tracker-item.other .tracker-value { color: #374151; }

.grievance-section {
  background: #f0fdf4;
  border: 2px solid #86efac;
}

.grievance-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.grievance-text {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #1f2937;
  margin: 0;
}

.grievance-text a {
  color: #3b82f6;
  font-weight: 600;
  text-decoration: none;
}

.grievance-text a:hover {
  text-decoration: underline;
}

.contact-box {
  background: white;
  border: 2px solid #d1fae5;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.contact-item {
  font-size: 0.95rem;
  color: #1f2937;
}

.contact-item strong {
  color: #065f46;
  min-width: 140px;
  display: inline-block;
}

.contact-item a {
  color: #3b82f6;
  text-decoration: none;
}

.contact-item a:hover {
  text-decoration: underline;
}

/* Tracker table styles */
.tracker-note {
  margin: 10px 0 18px 0;
  color: #374151;
  line-height: 1.5;
}

.tracker-subtitle {
  margin: 18px 0 8px 0;
  color: #0f172a;
  font-weight: 700;
}

.tracker-subtext {
  margin: 0 0 12px 0;
  color: #6b7280;
}

.tracker-table-wrap {
  overflow: auto;
  margin-bottom: 18px;
}

.tracker-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.tracker-table thead th {
  background: #dbeafe;
  color: #0f172a;
  font-weight: 700;
  padding: 12px 10px;
  text-align: left;
  border: 1px solid #e6eefb;
}

.tracker-table tbody td {
  padding: 12px 10px;
  border: 1px solid #eee;
  color: #111827;
}

.tracker-table tbody tr:nth-child(odd) {
  background: #fff;
}

.tracker-table .num {
  text-align: center;
}

.tracker-table tfoot .grand-total td {
  background: #efe8e0;
  font-weight: 700;
}

.doc-btn {
  background: #0e38d2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
}

.doc-btn:hover {
  background: #0b2ca8;
}

/* Simple modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}
.modal-content {
  width: 100%;
  max-width: 980px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}
.modal-header {
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:12px 16px;
  border-bottom:1px solid #eee;
}
.modal-header h3 { margin:0; }
.modal-close { background:transparent; border:0; cursor:pointer; color:#1f2937; font-weight:700; }
.modal-body { padding:12px; }

@media (max-width: 768px) {
  .main-title {
    font-size: 1.75rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .back-btn {
    position: static;
    margin-bottom: 20px;
    justify-content: center;
  }

  .disclosure-section {
    padding: 20px;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .info-item.full-width {
    grid-column: 1;
  }

  .complaint-tracker .tracker-grid {
    flex-direction: column;
  }

  .tracker-table { min-width: 100%; }
}
</style>
